import debounce from "lodash/debounce";
import { useState } from "react";
import dayjs from "dayjs";

import EntryCarouselSingle from "@components/EntryCarouselSingle";
import EntryThumbnail from "@components/EntryThumbnail";
import { getEntryCategoryArray } from "@lib/getEntryCategoryArray";
import { useInView } from "react-intersection-observer";
import useMeasure from "react-use-measure";
import EntryThumbnailHeader from "@components/EntryThumbnailHeader";
import { ArrowRight } from "@components/Icons";
import Target from "@components/Target";
import DotBorder from "@components/DotBorder";
import fallbackImage from "@images/abstract/floor_dark_green_1.FINAL.jpg";
import getImageBlurOptions from "@lib/getImageBlurOptions";
import { useEffect } from "react";

import clsx from "clsx";
import Image from 'next/image'

const EntryCarouselBlock = ({
	entries,
	aspectRatio = "square",
	isFlush = false,
	isFeatured = false,
	thumbnailImageHeight,
	onImageHeight = (height) => null,
	measureImageHeight = true,
}) => {
	const [imageRef, imageBounds] = useMeasure();

	useEffect(() => {
		if (measureImageHeight) onImageHeight(imageBounds.height);
	}, [measureImageHeight, onImageHeight, imageBounds.height]);

	const { ref, inView } = useInView();

	return (
		<EntryCarouselSingle
			isFlush={isFlush}
			thumbnailImageHeight={thumbnailImageHeight}
			isFeatured={isFeatured}
			columns={1}
		>
			{entries.map((entry, index) => {
				const image = !!entry.image ? entry.image[0] : null;
				const imageBlur = !!entry.imageBlur ? entry.imageBlur[0] : null;
				const target = { element: entry, type: "entry" };
				const categories = getEntryCategoryArray(entry);
				let resourceType = null;

				const parsedDate = dayjs(entry.postDate);
				{
					categories.map((category) => {
						if (category.slug == "webinars" || category.slug == "videos") {
							resourceType = "video";
						}
						if (
							category.slug == "threat-intelligence-reports" ||
							category.slug == "threat-reports" ||
							category.slug == "white-papers"
						) {
							resourceType = "downloadable";
						}
					});
				}
				return (
					<div
						key={entry.id}
						className={clsx(
							"EntryCarousel__slide md:block lg:flex bg-white-01"
						)}
					>
						<div
							className={clsx(
								"EntryThumbnail w-full max-w-[400px]",
								"transition-opacity duration-500 ease-out"
							)}
							ref={ref}
						>
							<div
								ref={imageRef}
								className={clsx("EntryThumbnail__imageContainer")}
							>
								<Target
									target={target}
									className={clsx("EntryThumbnail__imageWrapper", "block")}
								>
									{!!image && !!image.url && (
										<Image
											src={image.url}
											height={image.height}
											width={image.width}
											alt={image.title}
											layout="responsive"
											sizes={
												imageBounds.width !== undefined
													? `${Math.round(imageBounds.width)}px`
													: "100vw"
											}
											className={clsx("EntryThumbnail__image")}
											draggable={false}
											{...getImageBlurOptions(image, imageBlur)}
										/>
									)}
									{!image && (
										<div
											className={clsx(
												"EntryThumbnail__fallbackImage",
												"aspect-w-16",
												{
													"aspect-h-10": aspectRatio === "landscape",
													"aspect-h-16": aspectRatio === "square",
												}
											)}
										>
											<Image
												src={fallbackImage}
												alt={entry.title}
												layout="fill"
												objectFit="cover"
												draggable={false}
												className={clsx("EntryThumbnail__image--fallback")}
											/>
										</div>
									)}
								</Target>
							</div>
						</div>

						<div
							className={clsx(
								"EntryThumbnail__caption md:w-full lg:w-auto lg:p-10",
								"relative"
							)}
						>
							<EntryThumbnailHeader
								heading={entry.title}
								categories={getEntryCategoryArray(entry)}
								target={target}
								date={parsedDate.format("MMMM D, YYYY")}
							/>
							<div
								className={clsx(
									"EntryThumbnail__summary line-clamp-4",
									"max-w-lg",
									"lg:pr-7.5"
								)}
							>
								{entry.summary}
							</div>
							<Target
								target={target}
								className={clsx(
									"EntryThumbnail__target",
									"flex items-center",
									"md:pl-0 lg:pr-7.5 pt-7.5",
									"hover-underline",
									"group"
								)}
							>
								<div
									className={clsx(
										"EntryThumbnail__targetIcon",
										"mr-4",
										"transition-transform group-hover:translate-x-1"
									)}
								>
									<ArrowRight />
								</div>
								<span className={clsx("inline-block pb-[2px]")}>
									{resourceType && resourceType == "video" && "Watch Now"}
									{resourceType &&
										resourceType == "downloadable" &&
										"Download Now"}
									{!resourceType && "Read More"}
								</span>
							</Target>
						</div>
					</div>
				);
			})}
		</EntryCarouselSingle>
	);
};

export default EntryCarouselBlock;
